import type { ReactNode } from 'react';

import useFormatNumber, { classicFormat, percentFormat } from '@/hooks/format/useFormatNumber';
import useTranslation from '@/utils/hooks/translation/useTranslation';
//hooks
import useActiveSocialProfile from '@/utils/hooks/user/useActiveSocialProfile';
import useIsGroup from '@/utils/hooks/user/useIsGroup';

//types
import type { UserNumberFormatType } from '@/types/userType';
import type { AlbumCriteria } from '@/lib/albums/types';

// Lib
import {
    AlbumCardAnalyticsIcons,
    AlbumCardAnalyticsIconsForGroups,
    AlbumCardAnalyticsIconsForGroupStories
} from '@/lib/albums/albumCardIconType';

//component
import Icon from '@/components/base/Icon';
import Tooltip from '@/components/base/Tooltip';

const defaultStyles =
    'flex flex-col h-full bg-white px-3 pt-3 space-y-5 dark:bg-icoDarkMode-anthracite overflow-hidden rounded-md transition duration-200 hover:shadow-icoXL dark:hover:shadow-icoDarkXL';

type CardProps = {
    children: ReactNode;
    className?: string;
};
const Card = ({ children, className = defaultStyles }: CardProps) => {
    return <div className={className}>{children}</div>;
};

type HeaderProps = {
    children: ReactNode;
    className?: string;
};

const Header = ({ children, className }: HeaderProps) => {
    return <div className={className}>{children}</div>;
};

Card.Header = Header;

export type AlbumAnalyticsType = Record<string, { value: number }>;
type FooterProps = {
    analytics: AlbumAnalyticsType;
    userNumbersFormat: UserNumberFormatType;
    className?: string;
    children?: ReactNode;
    criteria?: AlbumCriteria;
};

const Footer = ({
    analytics,
    userNumbersFormat,
    className = '',
    children,
    criteria
}: FooterProps) => {
    const { activeSocialProfile } = useActiveSocialProfile();
    const network = activeSocialProfile?.social_network_type;
    const isGroup = useIsGroup();
    const isStoriesCampaign = criteria?.album_type === 'stories';
    const availableIcons = isGroup
        ? isStoriesCampaign
            ? AlbumCardAnalyticsIconsForGroupStories
            : AlbumCardAnalyticsIconsForGroups
        : AlbumCardAnalyticsIcons[network];
    const { formatNumber } = useFormatNumber(userNumbersFormat);
    const { t } = useTranslation('content');

    const formatValueAnalytics = (value: number, type: string): number | string => {
        if (value === null) {
            return 'N/A';
        }
        if (type === 'average_engagement_rate_per_post' || type === 'average_stories_reach_rate') {
            return formatNumber(value, percentFormat);
        }
        return formatNumber(value, classicFormat);
    };

    return (
        <div className={className}>
            {children ? (
                children
            ) : (
                <>
                    <div className="mb-m h-px w-auto bg-icoGray-300 dark:bg-icoDarkMode-beton" />
                    <div className="mb-m flex w-auto justify-center space-x-4">
                        {Object.entries(analytics)?.map((item, index) => (
                            <Tooltip
                                key={index}
                                content={t(`analytics.post_engagements.${item[0]}`)}
                                placement="top">
                                <div className="flex items-center space-x-2 text-xs">
                                    <Icon icon={availableIcons[item[0]]} className="text-icoBlue" />
                                    <span>{formatValueAnalytics(item[1].value, item[0])}</span>
                                </div>
                            </Tooltip>
                        ))}
                    </div>
                </>
            )}
        </div>
    );
};

Card.Footer = Footer;

type ContentProps = {
    title: string;
    countMedias: number;
    style?: string;
    children?: ReactNode;
    criteria: AlbumCriteria;
};
const Content = ({
    title,
    countMedias,
    criteria,
    style = 'flex flex-col items-center space-y-2',
    children
}: ContentProps) => {
    const { t } = useTranslation('content');
    if (children) {
        return <div className={style}>{children}</div>;
    }

    return (
        <div className={style}>
            <p className="w-full truncate text-center font-GBold text-xl" title={title}>
                {title}
            </p>
            <span className="w-full text-center text-sm text-icoGray-500 dark:text-icoDarkMode-wolf">
                {(() => {
                    const isStories = criteria?.album_type === 'stories';
                    return isStories
                        ? t('stories_count', { count: countMedias })
                        : t('posts_count', { count: countMedias });
                })()}
            </span>
        </div>
    );
};

Card.Content = Content;

export default Card;
